import {
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  createContext,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import { createStructuredSelector } from 'reselect';

import { get, find, isEmpty } from 'lodash';

import { useAppDispatch } from 'store';
import { openModal } from 'containers/App/actions/ui';
import {
  isOnboarding,
  isSettingPassword,
  isPublicPortalPath,
} from 'containers/App/utils/helpers';
import { isIntegrationIframe } from 'utils/checkEnvironment';
import {
  companySelector,
  companyChannelSelector,
} from 'containers/App/selectors';
import { userRole } from 'utils/authorization/utils/auth';
import { listsEnabled } from 'components/utils/subscriptionPlans';
import { IS_MOBILE as isMobile } from 'utils/constants';

import {
  fetchIntegrations,
  fetchIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  validateCredentials,
  fetchResources,
  fetchResource,
  refreshSettings,
  forceSync,
  deploySalesforceMetadata,
} from './actions';
import { fetchBullhornCorporateUsers } from '../actions';
import {
  INTEGRATION_SYNC_UPDATE,
  INTEGRATION_SYNC_PROGRESS_UPDATE,
} from './constants';
import { integrationsReducer, initialState } from './reducer';

function isBullhornSettingsPage() {
  return window.location.pathname.startsWith('/settings/integrations/bullhorn');
}

export const IntegrationsContext = createContext({});

const IntegrationsProviderComponent = (props) => {
  const {
    children,
    location,
    company,
    channel,
    handleOpenModal,
    handleRedirect,
  } = props;
  const [state, dispatch] = useReducer(integrationsReducer, initialState);
  const appDispatch = useAppDispatch();

  const onSuccessRedirect = useCallback(
    (response) => {
      const { type } = response;
      const bullhornAuthRedirect = sessionStorage.getItem(
        'BullhornAuthRedirect'
      );
      const salesforceAuthRedirect = sessionStorage.getItem(
        'SalesforceAuthRedirect'
      );

      if (type === 'bullhorn' && bullhornAuthRedirect) {
        handleRedirect(bullhornAuthRedirect);
        sessionStorage.removeItem('BullhornAuthRedirect');
      } else if (type === 'salesforce' && salesforceAuthRedirect) {
        handleRedirect(salesforceAuthRedirect);
        sessionStorage.removeItem('SalesforceAuthRedirect');
      } else {
        handleRedirect(`/settings/integrations/${type}`);
      }

      return response;
    },
    [handleRedirect]
  );

  const onFetchIntegration = useCallback(
    (type) => fetchIntegration(type)(dispatch),
    []
  );

  const onCreateIntegration = useCallback(
    (integrationData) =>
      createIntegration(integrationData)(dispatch, appDispatch).then(
        onSuccessRedirect
      ),
    [onSuccessRedirect, appDispatch]
  );

  const onUpdateIntegration = useCallback(
    (id, integrationData) =>
      updateIntegration(id, integrationData)(dispatch).then(onSuccessRedirect),
    [onSuccessRedirect]
  );

  const onDeleteIntegration = useCallback(
    (id) => deleteIntegration(id)(dispatch),
    []
  );

  const onValidateCredentials = useCallback(
    (type, credentials) => validateCredentials(type, credentials)(dispatch),
    []
  );

  const onFetchResources = useCallback(
    (integrationId, resourceDetails) =>
      fetchResources(integrationId, resourceDetails)(dispatch),
    []
  );

  const onFetchResource = useCallback(
    (integrationId, resourceDetails) =>
      fetchResource(integrationId, resourceDetails)(dispatch),
    []
  );

  const onRefreshSettings = useCallback(
    (type) => {
      const integrationId = get(state, `integrations.${type}.id`);
      if (integrationId) {
        return refreshSettings(integrationId)(dispatch);
      }
      return Promise.resolve();
    },
    [state]
  );

  const onForceSync = useCallback(
    (id, params) => forceSync(id, params)(dispatch),
    []
  );

  const onDeploySalesforceMetadata = useCallback(
    (id, sfType) => deploySalesforceMetadata(id, sfType)(dispatch),
    []
  );

  const onFetchBullhornCorporateUsers = useCallback(
    (params) => appDispatch(fetchBullhornCorporateUsers(params)),
    [appDispatch]
  );

  const onOpenBullhornAuthModal = useCallback(
    () =>
      handleOpenModal({
        type: 'bullhornAuth',
        integration: find(state.integrations, {
          type: 'bullhorn',
        }),
      }),
    [handleOpenModal, state.integrations]
  );

  const value = useMemo(
    () => ({
      integrations: state.integrations,
      loading: state.loading,
      onFetchIntegration,
      onCreateIntegration,
      onUpdateIntegration,
      onDeleteIntegration,
      onValidateCredentials,
      onFetchResources,
      onFetchResource,
      onRefreshSettings,
      onForceSync,
      onDeploySalesforceMetadata,
      onFetchBullhornCorporateUsers,
      onOpenBullhornAuthModal,
    }),
    [
      state.integrations,
      state.loading,
      onFetchIntegration,
      onCreateIntegration,
      onUpdateIntegration,
      onDeleteIntegration,
      onValidateCredentials,
      onFetchResources,
      onFetchResource,
      onRefreshSettings,
      onForceSync,
      onDeploySalesforceMetadata,
      onFetchBullhornCorporateUsers,
      onOpenBullhornAuthModal,
    ]
  );

  useEffect(() => {
    if (isEmpty(company)) return;

    fetchIntegrations()(dispatch).then(({ integrations: data }) => {
      if (
        !isBullhornSettingsPage() &&
        !isSettingPassword(location) &&
        !isOnboarding(location) &&
        !isPublicPortalPath(location) &&
        !isMobile
      ) {
        const bullhornIntegration = find(data, {
          type: 'bullhorn',
          connected: false,
        });

        if (
          bullhornIntegration &&
          (isIntegrationIframe(window) || userRole() === 'owner') &&
          listsEnabled(company)
        ) {
          handleOpenModal({
            type: 'bullhornAuth',
            integration: bullhornIntegration,
          });
          return;
        }

        const salesforceIntegration = find(data, {
          type: 'salesforce',
          connected: false,
        });

        if (
          salesforceIntegration &&
          (isIntegrationIframe(window) || userRole() === 'owner') &&
          listsEnabled(company)
        ) {
          handleOpenModal({
            type: 'salesforceAuth',
            integration: salesforceIntegration,
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, handleOpenModal]);

  useEffect(() => {
    if (!channel) return undefined;

    const syncUpdateRef = channel.on(
      'integration:sync_integration_update',
      (data) => {
        dispatch({
          type: INTEGRATION_SYNC_UPDATE,
          payload: data,
        });
      }
    );
    const syncProgressRef = channel.on(
      'integration:sync_progress_update',
      (data) => {
        dispatch({
          type: INTEGRATION_SYNC_PROGRESS_UPDATE,
          payload: data,
        });
      }
    );

    return () => {
      channel.off('integration:sync_integration_update', syncUpdateRef);
      channel.off('integration:sync_progress_update', syncProgressRef);
    };
  }, [channel]);

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};

IntegrationsProviderComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  location: PropTypes.object,
  company: PropTypes.object,
  channel: PropTypes.object,
  handleOpenModal: PropTypes.func.isRequired,
  handleRedirect: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  company: companySelector,
  channel: companyChannelSelector,
});

const mapDispatchToProps = (dispatch) => ({
  handleOpenModal: (modal) => dispatch(openModal(modal)),
  handleRedirect: (path) => dispatch(push(path)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const IntegrationsProvider = compose(
  withConnect,
  withRouter
)(IntegrationsProviderComponent);
